import { createAsyncThunk } from "@reduxjs/toolkit";
import toastHelper from "../../Util/toastHelper";
import API from "../../API/API";
import axios from "axios";
import { NavigateFunction } from "react-router";

export const getProfile = createAsyncThunk(
  "profile/getProfile",
  async (params:{navigate: NavigateFunction}, { rejectWithValue }) => {
    try {
      const { data } = await axios({
        url: API.members.profile.url,
        method: API.members.profile.method,
      });

      if(data?.isCancelSubscription === true) {
        params.navigate("/resubscribe");
      }

      return data;
    } catch (error: any) {
      if (error?.response?.data?.message) {
        toastHelper.error(error?.response?.data?.message);
      } else {
        toastHelper.error("Something went wrong");
      }
      return rejectWithValue(JSON.stringify(error));
    }
  },
);

export const getProfileById = createAsyncThunk(
  "profile/getProfileById",
  async (params: { memberId: number }, { rejectWithValue }) => {
    try {
      const { data } = await axios({
        url: API.admin.getMemberById.url + params.memberId,
        method: API.members.profile.method,
      });

      return data?.member;
    } catch (error: any) {
      if (error?.response?.data?.message) {
        toastHelper.error(error?.response?.data?.message);
      } else {
        toastHelper.error("Something went wrong");
      }
      return rejectWithValue(JSON.stringify(error));
    }
  },
);

export const updateProfile = createAsyncThunk(
  "profile/updateProfile",
  async (
    params: {
      firstName: string;
      lastName: string;
      email: string;
      idNumber: string;
      phonenumber: string;
      navigate: NavigateFunction;
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const { data } = await axios({
        url: API.members.updateProfile.url,
        method: API.members.updateProfile.method,
        data: params,
      });
      dispatch(getProfile({ navigate: params.navigate }));

      toastHelper.success("Profile updated successfully");
      return data;
    } catch (error: any) {
      if (error?.response?.data?.message) {
        toastHelper.error(error?.response?.data?.message);
      } else {
        toastHelper.error("Something went wrong");
      }
      return rejectWithValue(JSON.stringify(error));
    }
  },
);

export const updatePassword = createAsyncThunk(
  "profile/updatePassword",
  async (
    params: {
      oldPassword: string;
      newPassword: string;
      confirmPassword: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await axios({
        url: API.members.updatePassword.url,
        method: API.members.updatePassword.method,
        data: params,
      });

      toastHelper.success("Password updated successfully");

      return data;
    } catch (error: any) {
      if (error?.response?.data?.message) {
        toastHelper.error(error?.response?.data?.message);
      } else {
        toastHelper.error("Something went wrong");
      }
      return rejectWithValue(JSON.stringify(error));
    }
  },
);

export const addCard = createAsyncThunk(
  "profile/addCard",
  async (
    params: {
      cardNumber: string;
      cardName: string;
      expiry: string;
      cvv: string;
      cleanupHandler: () => void;
      navigate: NavigateFunction;
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const { data } = await axios({
        url: API.members.addCard.url,
        method: API.members.addCard.method,
        data: {
          cardNumber: params.cardNumber,
          cardName: params.cardName,
          expiry: params.expiry,
          cvv: params.cvv,
        },
      });
      toastHelper.success("Card added successfully");
      dispatch(getProfile({ navigate: params.navigate }));
      params.cleanupHandler();
      return data;
    } catch (error: any) {
      if (error?.response?.data?.message) {
        toastHelper.error(error?.response?.data?.message);
      } else {
        toastHelper.error("Something went wrong");
      }
      return rejectWithValue(JSON.stringify(error));
    }
  },
);

export const setDefaultCard = createAsyncThunk(
  "profile/setDefaultCard",
  async (params: { memberCCInfoId: number, navigate: NavigateFunction }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios({
        url: API.members.setDefaultCard.url,
        method: API.members.setDefaultCard.method,
        data: params,
      });
      dispatch(getProfile({ navigate: params.navigate }));
      return data;
    } catch (error: any) {
      if (error?.response?.data?.message) {
        toastHelper.error(error?.response?.data?.message);
      } else {
        toastHelper.error("Something went wrong");
      }
      return rejectWithValue(JSON.stringify(error));
    }
  },
);

export const deleteCard = createAsyncThunk(
  "profile/deleteCard",
  async (
    params: {
      id: string;
      successHandler: () => void;
      navigate: NavigateFunction;
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const { data } = await axios({
        url: API.members.deleteCard.url + params.id,
        method: API.members.deleteCard.method,
      });
      dispatch(getProfile({ navigate: params.navigate }));
      params.successHandler();
      return data;
    } catch (error: any) {
      if (error?.response?.data?.message) {
        toastHelper.error(error?.response?.data?.message);
      } else {
        toastHelper.error("Something went wrong");
      }
      return rejectWithValue(JSON.stringify(error));
    }
  },
);

export const getUpgradePackagesList = createAsyncThunk(
  "profile/getUpgradePackagesList",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios({
        url: API.members.getUpgradePackagesList.url,
        method: API.members.getUpgradePackagesList.method,
      });

      return data;
    } catch (error: any) {
      if (error?.response?.data?.message) {
        toastHelper.error(error?.response?.data?.message);
      } else {
        toastHelper.error("Something went wrong");
      }
      return rejectWithValue(JSON.stringify(error));
    }
  },
);

export const upgradePackage = createAsyncThunk(
  "profile/upgradePackage",
  async (
    params: {
      newPackageId: number;
      signature: string;
      memberCCInfoId: number;
      familyMember: any[];
      successHandler: () => void;
      navigate: NavigateFunction;
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const { data } = await axios({
        url: API.members.upgradePackage.url,
        method: API.members.upgradePackage.method,
        data: params,
      });
      dispatch(getProfile({ navigate: params.navigate }));
      params.successHandler();
      return data;
    } catch (error: any) {
      if (error?.response?.data?.message) {
        toastHelper.error(error?.response?.data?.message);
      } else {
        toastHelper.error("Something went wrong");
      }
      return rejectWithValue(JSON.stringify(error));
    }
  },
);

export const cancelSubscription = createAsyncThunk(
  "profile/cancelSubscription",
  async (params: { navigate: NavigateFunction }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios({
        url: API.members.cancelSubscription.url,
        method: API.members.cancelSubscription.method,
      });
      toastHelper.success(data?.message);
      dispatch(getProfile({ navigate: params.navigate }));
      return data;
    } catch (error: any) {
      if (error?.response?.data?.message) {
        toastHelper.error(error?.response?.data?.message);
      } else {
        toastHelper.error("Something went wrong");
      }
      return rejectWithValue(JSON.stringify(error));
    }
  },
);
